import React, { useState, useRef, useEffect } from "react";
import Header from "../components/Header";
import axios from "axios";

const DatabasePage = () => {
  // Navigation tabs: 'upload' or 'unit'
  const [activeTab, setActiveTab] = useState("upload");

  // Upload Database States
  const [uploading, setUploading] = useState(false);
  const [uploadMessage, setUploadMessage] = useState({ text: "", type: "" });
  const fileInputRef = useRef(null);

  // Unit List States
  const [units, setUnits] = useState([]);
  const [loadingUnits, setLoadingUnits] = useState(false);
  const [unitNameInput, setUnitNameInput] = useState("");
  const [unitCodeInput, setUnitCodeInput] = useState("");
  const [addingUnit, setAddingUnit] = useState(false);
  const [unitMessage, setUnitMessage] = useState({ text: "", type: "" });

  // Inline Editing States
  const [editingUnitId, setEditingUnitId] = useState(null);
  const [editNameInput, setEditNameInput] = useState("");
  const [editCodeInput, setEditCodeInput] = useState("");
  const [savingEdit, setSavingEdit] = useState(false);

  // Fetch all units from backend
  const fetchUnits = async () => {
    setLoadingUnits(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/units`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      if (response.data.success) {
        setUnits(response.data.data || []);
      }
    } catch (error) {
      console.error("Error fetching units:", error);
    } finally {
      setLoadingUnits(false);
    }
  };

  useEffect(() => {
    if (activeTab === "unit") {
      fetchUnits();
    }
  }, [activeTab]);

  // Handle excel upload
  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const fileExtension = file.name.split(".").pop().toLowerCase();
    if (fileExtension !== "xlsx" && fileExtension !== "xls") {
      setUploadMessage({
        text: "File harus berupa Excel (.xlsx atau .xls)",
        type: "error",
      });
      e.target.value = "";
      return;
    }

    const confirmUpload = window.confirm(
      "Apakah Anda yakin ingin mengupload database baru? Data dengan IDPEL yang sama akan diperbarui, dan IDPEL baru akan ditambahkan."
    );

    if (!confirmUpload) {
      e.target.value = "";
      return;
    }

    const formData = new FormData();
    formData.append("file", file);

    setUploading(true);
    setUploadMessage({ text: "Mengupload dan menyinkronkan database...", type: "info" });

    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/upload-database`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.data.success) {
        setUploadMessage({
          text: response.data.message || "Database berhasil diunggah!",
          type: "success",
        });
      } else {
        setUploadMessage({
          text: response.data.message || "Gagal mengunggah database.",
          type: "error",
        });
      }
    } catch (error) {
      console.error("Error uploading database:", error);
      const errorMsg =
        error.response?.data?.error ||
        error.response?.data?.message ||
        "Terjadi kesalahan server saat mengunggah.";
      setUploadMessage({
        text: `Gagal: ${errorMsg}`,
        type: "error",
      });
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const triggerFileInput = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  // Add new Unit
  const handleAddUnit = async (e) => {
    e.preventDefault();
    if (!unitNameInput.trim() || !unitCodeInput.trim()) return;

    setAddingUnit(true);
    setUnitMessage({ text: "", type: "" });

    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/units`,
        {
          name: unitNameInput.trim(),
          code: unitCodeInput.trim(),
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.data.success) {
        setUnitMessage({
          text: response.data.message || "Unit berhasil ditambahkan!",
          type: "success",
        });
        setUnitNameInput("");
        setUnitCodeInput("");
        fetchUnits(); // refresh units list
      } else {
        setUnitMessage({
          text: response.data.message || "Gagal menambahkan unit.",
          type: "error",
        });
      }
    } catch (error) {
      console.error("Error adding unit:", error);
      const errorMsg =
        error.response?.data?.message || "Terjadi kesalahan saat menambahkan unit.";
      setUnitMessage({
        text: errorMsg,
        type: "error",
      });
    } finally {
      setAddingUnit(false);
    }
  };

  // Delete Unit
  const handleDeleteUnit = async (id, name) => {
    const confirmDelete = window.confirm(`Apakah Anda yakin ingin menghapus unit "${name}"?`);
    if (!confirmDelete) return;

    try {
      const token = localStorage.getItem("token");
      const response = await axios.delete(
        `${process.env.REACT_APP_API_URL}/api/units/${id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.data.success) {
        setUnitMessage({
          text: `Unit "${name}" berhasil dihapus.`,
          type: "success",
        });
        fetchUnits(); // refresh units list
      } else {
        setUnitMessage({
          text: response.data.message || "Gagal menghapus unit.",
          type: "error",
        });
      }
    } catch (error) {
      console.error("Error deleting unit:", error);
      const errorMsg =
        error.response?.data?.message || "Gagal menghapus unit.";
      setUnitMessage({
        text: errorMsg,
        type: "error",
      });
    }
  };

  // Edit Unit Actions
  const startEditing = (unit) => {
    setEditingUnitId(unit.id);
    setEditNameInput(unit.name);
    setEditCodeInput(unit.code);
    setUnitMessage({ text: "", type: "" });
  };

  const cancelEditing = () => {
    setEditingUnitId(null);
    setEditNameInput("");
    setEditCodeInput("");
  };

  const handleSaveEdit = async (id) => {
    if (!editNameInput.trim() || !editCodeInput.trim()) return;

    setSavingEdit(true);
    setUnitMessage({ text: "", type: "" });

    try {
      const token = localStorage.getItem("token");
      const response = await axios.put(
        `${process.env.REACT_APP_API_URL}/api/units/${id}`,
        {
          name: editNameInput.trim(),
          code: editCodeInput.trim(),
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.data.success) {
        setUnitMessage({
          text: response.data.message || "Unit berhasil diperbarui!",
          type: "success",
        });
        setEditingUnitId(null);
        fetchUnits(); // refresh units list
      } else {
        setUnitMessage({
          text: response.data.message || "Gagal memperbarui unit.",
          type: "error",
        });
      }
    } catch (error) {
      console.error("Error updating unit:", error);
      const errorMsg =
        error.response?.data?.message || "Terjadi kesalahan saat memperbarui unit.";
      setUnitMessage({
        text: errorMsg,
        type: "error",
      });
    } finally {
      setSavingEdit(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <Header title={"Manajemen Database"} />
      
      <div className="px-6 pt-24 pb-12 max-w-5xl mx-auto">
        <div className="mb-8">
          <h2 className="text-2xl font-extrabold text-slate-900 tracking-tight">
            Manajemen Database Portal
          </h2>
          <p className="text-sm text-slate-500 mt-2 font-medium">
            Unggah spreadsheet pelanggan terbaru (.xlsx) atau kelola daftar unit kerja posko operasional untuk pilihan pembuatan tugas dan pendaftaran petugas.
          </p>
        </div>

        {/* Tab Buttons */}
        <div className="flex border-b border-slate-200 mb-8 bg-white p-1.5 rounded-xl border shadow-sm">
          <button
            onClick={() => setActiveTab("upload")}
            className={`flex-1 py-3 px-4 rounded-lg text-sm font-bold transition-all duration-200 flex items-center justify-center gap-2 ${
              activeTab === "upload"
                ? "bg-slate-900 text-white shadow-md"
                : "text-slate-500 hover:text-slate-800 hover:bg-slate-50"
            }`}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
            </svg>
            Upload Database Pelanggan
          </button>
          
          <button
            onClick={() => setActiveTab("unit")}
            className={`flex-1 py-3 px-4 rounded-lg text-sm font-bold transition-all duration-200 flex items-center justify-center gap-2 ${
              activeTab === "unit"
                ? "bg-slate-900 text-white shadow-md"
                : "text-slate-500 hover:text-slate-800 hover:bg-slate-50"
            }`}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
            </svg>
            Daftar Unit Kerja
          </button>
        </div>

        {/* Tab 1: Upload Database */}
        {activeTab === "upload" && (
          <div className="bg-white p-6 md:p-8 rounded-2xl border border-slate-200 shadow-sm">
            <h3 className="text-lg font-bold text-slate-900 mb-4">Upload File Excel</h3>

            <div className="bg-amber-50 border-l-4 border-amber-500 p-4 mb-6 rounded-xl flex gap-3">
              <svg className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              <div>
                <h4 className="text-sm font-bold text-amber-800">Informasi Singkronisasi</h4>
                <p className="text-xs text-amber-700 font-medium mt-1 leading-relaxed">
                  Mengunggah database baru tidak akan menghapus data yang sudah ada di sistem. Data dengan IDPEL yang sama akan diperbarui dengan data Excel terbaru, sedangkan IDPEL baru akan ditambahkan secara otomatis.
                </p>
              </div>
            </div>

            <div className="p-4 bg-slate-50 border border-slate-200 rounded-xl mb-6">
              <h4 className="text-xs font-extrabold text-slate-500 uppercase tracking-wider mb-2">Struktur Kolom Header Excel:</h4>
              <p className="text-xs font-mono text-slate-700 bg-white p-3 rounded-lg border border-slate-200 leading-relaxed font-semibold">
                UNIT, IDPEL, NAMA, ALAMAT, TARIF, DAYA, TOGANMET, ROGANMET, LAYANAN, GARDU, POSKO, LATITUDE, LONGITUDE
              </p>
            </div>

            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileChange}
              accept=".xlsx, .xls"
              className="hidden"
            />

            <button
              onClick={triggerFileInput}
              disabled={uploading}
              className={`w-full py-6 px-4 rounded-xl border-2 border-dashed font-bold transition flex flex-col items-center justify-center gap-3 ${
                uploading
                  ? "bg-slate-50 border-slate-200 text-slate-400 cursor-not-allowed"
                  : "bg-indigo-50/30 border-indigo-200 text-indigo-700 hover:bg-indigo-50 hover:border-indigo-300"
              }`}
            >
              {uploading ? (
                <>
                  <div className="w-8 h-8 border-3 border-slate-300 border-t-slate-600 rounded-full animate-spin"></div>
                  <span className="text-sm font-bold text-slate-600">Menyinkronkan Spreadsheet...</span>
                </>
              ) : (
                <>
                  <div className="w-12 h-12 rounded-full bg-indigo-50 flex items-center justify-center text-indigo-600">
                    <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                    </svg>
                  </div>
                  <div className="text-center">
                    <span className="text-sm block">Pilih File Excel & Sinkronkan</span>
                    <span className="text-xs text-slate-400 font-medium block mt-1">Mendukung format file .xlsx dan .xls</span>
                  </div>
                </>
              )}
            </button>

            {uploadMessage.text && (
              <div
                className={`mt-6 p-4 rounded-xl text-sm font-bold text-center border ${
                  uploadMessage.type === "success"
                    ? "bg-emerald-50 text-emerald-800 border-emerald-200"
                    : uploadMessage.type === "error"
                    ? "bg-rose-50 text-rose-800 border-rose-200"
                    : "bg-indigo-50 text-indigo-800 border-indigo-200"
                }`}
              >
                {uploadMessage.text}
              </div>
            )}
          </div>
        )}

        {/* Tab 2: Unit List Management */}
        {activeTab === "unit" && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            
            {/* Table Column (2/3 width) */}
            <div className="md:col-span-2 bg-white p-6 rounded-2xl border border-slate-200 shadow-sm flex flex-col justify-between">
              <div>
                <h3 className="text-lg font-bold text-slate-900 mb-4">Daftar Unit Terdaftar</h3>
                
                {loadingUnits ? (
                  <div className="py-8 text-center">
                    <div className="w-8 h-8 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin mx-auto mb-2"></div>
                    <span className="text-xs font-semibold text-slate-400">Memuat daftar unit...</span>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse">
                      <thead>
                        <tr className="border-b border-slate-100">
                          <th className="pb-3 text-xs font-extrabold text-slate-400 uppercase tracking-wider w-12">No</th>
                          <th className="pb-3 text-xs font-extrabold text-slate-400 uppercase tracking-wider w-36">Kode Unit</th>
                          <th className="pb-3 text-xs font-extrabold text-slate-400 uppercase tracking-wider">Nama Unit Kerja</th>
                          <th className="pb-3 text-xs font-extrabold text-slate-400 uppercase tracking-wider text-right w-24">Aksi</th>
                        </tr>
                      </thead>
                      <tbody>
                        {units.map((unit, index) => {
                          const isEditing = unit.id === editingUnitId;
                          return (
                            <tr key={unit.id} className="hover:bg-slate-50/50 transition-colors border-b border-slate-100 last:border-0 animate-fadeIn">
                              <td className="py-3.5 text-sm font-semibold text-slate-500">{index + 1}</td>
                              
                              <td className="py-3.5 text-sm">
                                {isEditing ? (
                                  <input
                                    type="text"
                                    value={editCodeInput}
                                    onChange={(e) => setEditCodeInput(e.target.value)}
                                    disabled={savingEdit}
                                    className="p-1.5 border border-slate-200 rounded-lg text-sm font-bold uppercase font-mono w-full focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 focus:outline-none"
                                  />
                                ) : (
                                  <span className="font-bold text-indigo-600 font-mono tracking-wider uppercase">{unit.code}</span>
                                )}
                              </td>
                              
                              <td className="py-3.5 text-sm">
                                {isEditing ? (
                                  <input
                                    type="text"
                                    value={editNameInput}
                                    onChange={(e) => setEditNameInput(e.target.value)}
                                    disabled={savingEdit}
                                    className="p-1.5 border border-slate-200 rounded-lg text-sm font-bold uppercase w-full focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 focus:outline-none"
                                  />
                                ) : (
                                  <span className="font-bold text-slate-900 uppercase">{unit.name}</span>
                                )}
                              </td>
                              
                              <td className="py-3.5 text-right">
                                {isEditing ? (
                                  <div className="flex justify-end gap-1.5">
                                    <button
                                      onClick={() => handleSaveEdit(unit.id)}
                                      disabled={savingEdit}
                                      className="p-1.5 rounded-lg text-emerald-600 hover:bg-emerald-50 transition"
                                      title="Simpan"
                                    >
                                      {savingEdit ? (
                                        <div className="w-4 h-4 border-2 border-emerald-600 border-t-transparent rounded-full animate-spin"></div>
                                      ) : (
                                        <svg className="w-4.5 h-4.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M5 13l4 4L19 7" />
                                        </svg>
                                      )}
                                    </button>
                                    <button
                                      onClick={cancelEditing}
                                      disabled={savingEdit}
                                      className="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-50 transition"
                                      title="Batal"
                                    >
                                      <svg className="w-4.5 h-4.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M6 18L18 6M6 6l12 12" />
                                      </svg>
                                    </button>
                                  </div>
                                ) : (
                                  <div className="flex justify-end gap-1.5">
                                    <button
                                      onClick={() => startEditing(unit)}
                                      className="p-1.5 rounded-lg text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 transition"
                                      title="Edit Unit"
                                    >
                                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                      </svg>
                                    </button>
                                    <button
                                      onClick={() => handleDeleteUnit(unit.id, unit.name)}
                                      className="p-1.5 rounded-lg text-slate-400 hover:text-rose-600 hover:bg-rose-50 transition"
                                      title="Hapus Unit"
                                    >
                                      <svg className="w-4.5 h-4.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                      </svg>
                                    </button>
                                  </div>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                        {units.length === 0 && (
                          <tr>
                            <td colSpan="4" className="py-8 text-center text-sm font-medium text-slate-400">
                              Belum ada unit terdaftar.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>

            {/* Form Column (1/3 width) */}
            <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm h-fit space-y-4">
              <h3 className="text-lg font-bold text-slate-900">Tambah Unit</h3>
              
              <form onSubmit={handleAddUnit} className="space-y-4">
                <div className="flex flex-col gap-1.5">
                  <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Kode Unit:</label>
                  <input
                    type="text"
                    value={unitCodeInput}
                    onChange={(e) => setUnitCodeInput(e.target.value)}
                    placeholder="Contoh: UP3PKU"
                    required
                    className="w-full p-3 border border-slate-200 rounded-xl text-sm font-bold uppercase focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 focus:outline-none"
                  />
                </div>

                <div className="flex flex-col gap-1.5">
                  <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Nama Unit:</label>
                  <input
                    type="text"
                    value={unitNameInput}
                    onChange={(e) => setUnitNameInput(e.target.value)}
                    placeholder="Contoh: UP3 PEKANBARU"
                    required
                    className="w-full p-3 border border-slate-200 rounded-xl text-sm font-bold uppercase focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 focus:outline-none"
                  />
                </div>

                <button
                  type="submit"
                  disabled={addingUnit || !unitNameInput.trim() || !unitCodeInput.trim()}
                  className="w-full py-3 rounded-xl bg-indigo-600 text-white font-bold text-sm hover:bg-indigo-700 transition shadow-md hover:shadow-indigo-600/10 disabled:opacity-50 flex items-center justify-center gap-1.5"
                >
                  {addingUnit ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      Menyimpan...
                    </>
                  ) : (
                    <>
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M12 4v16m8-8H4" />
                      </svg>
                      Tambah Unit
                    </>
                  )}
                </button>
              </form>

              {unitMessage.text && (
                <div
                  className={`p-3 rounded-xl text-xs font-bold text-center border ${
                    unitMessage.type === "success"
                      ? "bg-emerald-50 text-emerald-800 border-emerald-200"
                      : "bg-rose-50 text-rose-800 border-rose-200"
                  }`}
                >
                  {unitMessage.text}
                </div>
              )}
            </div>

          </div>
        )}
      </div>
    </div>
  );
};

export default DatabasePage;
